import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/Dashboard.css';
import userlogo from '../../images/Ellipse 1.png';
import logo from '../../images/logo.png';
import Typography from '../../components/Typography';
import axios from 'axios';
import moment from 'moment';

const Navbar = ({ user, admin, onhandleMarkAllAsRead, onReaded, isSidbarOpen, toggleSidebar, closeSidbar }) => {
    const navigate = useNavigate();
    const userDropdownRef = useRef(null);
    const userButtonRef = useRef(null);
    const notificationDropdownRef = useRef(null);
    const notificationButtonRef = useRef(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const eventSourceRef = useRef(null);


    // Fetch initial notifications
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const fetchedNotifications = response.data.notifications || [];
                setNotifications(fetchedNotifications);
                const unread = fetchedNotifications.filter(notification => !notification.read).length;
                setUnreadCount(unread);
                // await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/events`, { headers: { Authorization: `Bearer ${token}` } })
            } catch (error) {
                console.error('Error fetching notifications:', error);
            }
        };

        fetchNotifications();
    }, []);

    // Set up SSE connection
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            // Since EventSource doesn't support headers, append the token as a query parameter
            const eventSourceUrl = `${process.env.REACT_APP_BACKEND_URL}/api/notifications/events?token=${token}`;
            eventSourceRef.current = new EventSource(eventSourceUrl);

            eventSourceRef.current.onopen = () => {
                console.log('SSE connection opened for user:');
            };

            eventSourceRef.current.onmessage = (event) => {
                try {
                    const newNotification = JSON.parse(event.data);
                    console.log('Received new notification via SSE:', newNotification);
                    if (newNotification && !notifications.some(n => n._id === newNotification._id)) {
                        setNotifications(prev => {
                            const updatedNotifications = [...prev, newNotification];
                            console.log('Updated notifications:', updatedNotifications);
                            return updatedNotifications;
                        });
                        if (!newNotification.read) {
                            setUnreadCount(prev => {
                                const newCount = prev + 1;
                                console.log('Updated unread count:', newCount);
                                return newCount;
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error parsing SSE message:', error);
                }
            };

            eventSourceRef.current.onerror = (error) => {
                console.error('EventSource failed:', error);
                eventSourceRef.current.close();
            };
        }

        return () => {
            if (eventSourceRef.current) {
                console.log('Closing SSE connection');
                eventSourceRef.current.close();
            }
        };
    }, [notifications]);

    // Handle user dropdown
    useEffect(() => {
        const handleClickOutsideUser = (event) => {
            if (
                userDropdownRef.current &&
                userButtonRef.current &&
                !userDropdownRef.current.contains(event.target) &&
                !userButtonRef.current.contains(event.target)
            ) {
                userDropdownRef.current.classList.add('hidden');
            }
        };

        const toggleUserDropdown = (event) => {
            event.stopPropagation();
            if (userDropdownRef.current.classList.contains('hidden')) {
                userDropdownRef.current.classList.remove('hidden');
            } else {
                userDropdownRef.current.classList.add('hidden');
            }
        };

        const userButton = userButtonRef.current;
        if (userButton) {
            userButton.addEventListener('click', toggleUserDropdown);
        }

        window.addEventListener('click', handleClickOutsideUser);

        return () => {
            if (userButton) {
                userButton.removeEventListener('click', toggleUserDropdown);
            }
            window.removeEventListener('click', handleClickOutsideUser);
        };
    }, []);

    // Handle notification dropdown
    useEffect(() => {

        const handleClickOutsideNotification = (event) => {
            if (
                notificationDropdownRef.current &&
                notificationButtonRef.current &&
                !notificationDropdownRef.current.contains(event.target) &&
                !notificationButtonRef.current.contains(event.target)
            ) {
                notificationDropdownRef.current.classList.add('hidden');
            }
        };

        const toggleNotificationDropdown = async (event) => {
            event.stopPropagation();
            if (notificationDropdownRef.current.classList.contains('hidden')) {
                notificationDropdownRef.current.classList.remove('hidden');
                const unreadNotifications = notifications.filter(n => !n.read);
                if (unreadNotifications.length > 0) {
                    try {
                        const token = localStorage.getItem('token');
                        await Promise.all(
                            unreadNotifications.map(notification =>
                                axios.put(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/notifications/user/${notification._id}/read`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` } }
                                )
                            )
                        );
                        setNotifications(notifications.map(n => ({ ...n, read: true })));
                        setUnreadCount(0);
                        if (onhandleMarkAllAsRead) {
                            onhandleMarkAllAsRead();
                        }
                    } catch (error) {
                        console.error('Error marking notifications as read:', error);
                    }
                }
            } else {
                notificationDropdownRef.current.classList.add('hidden');
            }
        };

        const notificationButton = notificationButtonRef.current;
        if (notificationButton) {
            notificationButton.addEventListener('click', toggleNotificationDropdown);
        }

        window.addEventListener('click', handleClickOutsideNotification);

        return () => {
            if (notificationButton) {
                notificationButton.removeEventListener('click', toggleNotificationDropdown);
            }
            window.removeEventListener('click', handleClickOutsideNotification);
        };
    }, [notifications, onhandleMarkAllAsRead]);

    useEffect(() => {
        if (onReaded) {
            setUnreadCount(0);
        }
    }, [onReaded]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('user');
        navigate('/login');
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }
    };

    const relativeTime = (value) => {
        return value ? moment(value, 'hh:mm A | DD MMM YY').fromNow() : '';
    }

    return (
        <nav className="px-[24px]  py-[22px] box-border lg:bg-black bg-[#121212] h-[82px] lg:border-b border-[#353535]">
            <div className="flex flex-wrap items-center my-auto md:flex-row flex-row-reverse justify-between mx-auto">
                <div className="gap-2 cursor-pointer lg:hidden inline-flex items-center" onClick={toggleSidebar}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="24" viewBox="0 0 44 24" fill="none">
                        <mask id="mask0_4548_1728" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="44" height="24">
                            <rect width="44" height="24" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_4548_1728)">
                            <path d="M9 15V13.6H35V15H9ZM9 9.4V8H35V9.4H9Z" fill="white" />
                        </g>
                    </svg>
                    <Typography text={'Menu'} color={'#FFF'} size={'16px'} family={'Supply'} transform={'uppercase'} weight={400} lineheight={'170%'} />
                </div>
                <Link to={'https://ashcapital.com'} className="flex items-center space-x-3 rtl:space-x-reverse">
                    <img src={logo} alt="Ash Logo" />
                </Link>
                <div className={`${admin && 'hidden'} h-full max-h-[30px] md:flex hidden items-center md:order-2 space-x-3 md:space-x-[24px] rtl:space-x-reverse`}>
                    <button type="button" onClick={() => {user?.role === 'admin' ? navigate('/admin/settings') : navigate('/settings')}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_650_1691)">
                                <path d="M22.3066 10.0509C22.2857 9.9453 22.2423 9.84543 22.1794 9.75807C22.1164 9.67072 22.0354 9.59793 21.9419 9.54467L19.1453 7.95092L19.1341 4.79904C19.1337 4.69049 19.1098 4.58331 19.064 4.4849C19.0182 4.38649 18.9516 4.29919 18.8688 4.22904C17.8543 3.37095 16.6861 2.71336 15.4263 2.29123C15.327 2.25765 15.2219 2.24522 15.1176 2.25475C15.0133 2.26428 14.9121 2.29555 14.8206 2.34654L11.9997 3.92342L9.17594 2.34373C9.0844 2.29245 8.98311 2.26094 8.87863 2.25125C8.77414 2.24156 8.66879 2.2539 8.56938 2.28748C7.31029 2.71232 6.14335 3.37246 5.13063 4.23279C5.04791 4.30284 4.98136 4.38999 4.93556 4.48823C4.88975 4.58647 4.86579 4.69347 4.86532 4.80186L4.85125 7.95654L2.05469 9.55029C1.96113 9.60356 1.88013 9.67634 1.8172 9.7637C1.75428 9.85105 1.71089 9.95093 1.69 10.0565C1.43406 11.3427 1.43406 12.6667 1.69 13.9528C1.71089 14.0584 1.75428 14.1583 1.8172 14.2456C1.88013 14.333 1.96113 14.4058 2.05469 14.459L4.85125 16.0528L4.8625 19.2056C4.86284 19.3142 4.88674 19.4213 4.93255 19.5197C4.97835 19.6182 5.04498 19.7055 5.12782 19.7756C6.14226 20.6337 7.31046 21.2913 8.57031 21.7134C8.66953 21.747 8.77468 21.7594 8.87899 21.7499C8.98329 21.7404 9.08445 21.7091 9.17594 21.6581L11.9997 20.0765L14.8234 21.6562C14.9352 21.7185 15.0611 21.7508 15.1891 21.75C15.271 21.75 15.3523 21.7367 15.43 21.7106C16.6888 21.286 17.8556 20.6265 18.8688 19.7672C18.9515 19.6971 19.018 19.61 19.0638 19.5117C19.1096 19.4135 19.1336 19.3065 19.1341 19.1981L19.1481 16.0434L21.9447 14.4497C22.0383 14.3964 22.1192 14.3236 22.1822 14.2363C22.2451 14.1489 22.2885 14.049 22.3094 13.9434C22.5639 12.6583 22.5629 11.3357 22.3066 10.0509ZM11.9997 15.75C11.258 15.75 10.533 15.53 9.9163 15.118C9.29962 14.7059 8.81897 14.1203 8.53514 13.435C8.25131 12.7498 8.17705 11.9958 8.32174 11.2684C8.46644 10.541 8.82359 9.87278 9.34804 9.34833C9.87249 8.82388 10.5407 8.46673 11.2681 8.32204C11.9955 8.17734 12.7495 8.2516 13.4348 8.53543C14.12 8.81926 14.7056 9.29991 15.1177 9.91659C15.5298 10.5333 15.7497 11.2583 15.7497 12C15.7497 12.9945 15.3546 13.9484 14.6513 14.6516C13.9481 15.3549 12.9943 15.75 11.9997 15.75Z" fill="#F5F5F5" />
                            </g>
                            <defs>
                                <clipPath id="clip0_650_1691">
                                    <rect width="24" height="24" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </button>
                    <div className="relative">
                        <button
                            ref={notificationButtonRef}
                            type="button"
                            className='flex items-center'
                            id="notifications"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <g clipPath="url(#clip0_650_1688)">
                                    <path d="M15.7499 21C15.7499 21.1989 15.6709 21.3897 15.5302 21.5303C15.3896 21.671 15.1988 21.75 14.9999 21.75H8.99989C8.80098 21.75 8.61021 21.671 8.46956 21.5303C8.32891 21.3897 8.24989 21.1989 8.24989 21C8.24989 20.8011 8.32891 20.6103 8.46956 20.4697C8.61021 20.329 8.80098 20.25 8.99989 20.25H14.9999C15.1988 20.25 15.3896 20.329 15.5302 20.4697C15.6709 20.6103 15.7499 20.8011 15.7499 21ZM20.7946 16.4944C20.2733 15.5981 19.4999 13.0622 19.4999 9.75C19.4999 7.76088 18.7097 5.85322 17.3032 4.4467C15.8967 3.04018 13.989 2.25 11.9999 2.25C10.0108 2.25 8.10311 3.04018 6.69659 4.4467C5.29007 5.85322 4.49989 7.76088 4.49989 9.75C4.49989 13.0631 3.72551 15.5981 3.2052 16.4944C3.07233 16.7222 3.00189 16.9811 3.00099 17.2449C3.00008 17.5086 3.06874 17.768 3.20005 17.9967C3.33135 18.2255 3.52065 18.4156 3.74886 18.5478C3.97708 18.6801 4.23613 18.7498 4.49989 18.75H19.4999C19.7636 18.7496 20.0225 18.6798 20.2506 18.5475C20.4787 18.4151 20.6678 18.225 20.799 17.9963C20.9302 17.7676 20.9988 17.5083 20.9979 17.2446C20.9969 16.9809 20.9265 16.7222 20.7936 16.4944H20.7946Z" fill="white" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_650_1688">
                                        <rect width="24" height="24" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                            {unreadCount > 0 && (
                                <span id="notificationCount" key={unreadCount} className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                        <div
                            ref={notificationDropdownRef}
                            className="z-50 hidden absolute top-6 right-0 my-4 text-base list-none min-w-64 max-h-96 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto bg-[#121212] divide-y divide-gray-100 rounded-lg shadow-sm animate-backdropFadeIn animate-modalSlideIn"
                            id="notification-dropdown"
                        >
                            {notifications.length === 0 ? (
                                <div className="px-4 py-3 text-center">
                                    <Typography text="No notifications" family="Supply" transform="uppercase" color="#FFF" size="14px" />
                                </div>
                            ) : (
                                notifications.map(notification => (
                                    <div key={notification._id} className="px-4 py-3">
                                        <Typography text={notification.title} family="Supply" transform="uppercase" color="#FFF" size="14px" />
                                        <Typography text={notification.message} family="Aeonik" color="#FFF" size="12px" />
                                        <Typography text={relativeTime(notification.delivery)} family="Aeonik" color="#888" size="10px" />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="relative">
                        <button
                            ref={userButtonRef}
                            type="button"
                            className="flex text-sm bg-gray-800 w-[38px] h-[38px] rounded-full md:me-0 focus:ring-gray-300 dark:focus:ring-gray-600"
                            id="user-menu-button"
                            aria-expanded="false"
                            data-dropdown-toggle="user-dropdown"
                            data-dropdown-placement="bottom"

                        >
                            <img className="rounded-full w-[38px] h-[38px]" src={process.env.REACT_APP_BACKEND_URL+user?.avatar || userlogo} width={38} height={38} alt="userlogo" />
                        </button>
                        <div
                            ref={userDropdownRef}
                            className="z-50 hidden absolute top-6 right-0 my-4 text-base list-none min-w-40 bg-[#121212] divide-y divide-gray-100 rounded-lg shadow-sm animate-backdropFadeIn animate-modalSlideIn"
                            id="user-dropdown"
                        >
                            <div className="px-4 py-3">
                                <span className="block text-sm text-center text-gray-900 dark:text-white">
                                    <Typography text={user?.name} family="Supply" transform="uppercase" />
                                </span>
                            </div>
                            <ul className="py-2 text-center" aria-labelledby="user-menu-button">
                                <li>
                                    <Link
                                        to={user?.role === 'admin' ? '/admin' : '/dashboard'}
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white"
                                    >
                                        <Typography text={user?.role === 'admin' ? 'Admin' : 'Dashboard'} family="Supply" transform="uppercase" />
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to={user?.role === 'admin' ? '/admin/settings' : '/settings'}
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white"
                                    >
                                        <Typography text="Settings" family="Supply" transform="uppercase" />
                                    </Link>
                                </li>
                                <li>
                                    <button
                                        onClick={handleLogout}
                                        className="block text-center w-full mx-auto px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white"
                                    >
                                        <Typography text="Sign out" family="Supply" transform="uppercase" />
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;